import { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSchemaBySlug } from '../engine/registry';
import { useAssessmentRunner } from '../engine/runner';
import { useAutosave } from '../engine/autosave';
import { saveDraft } from '../engine/persistence';
import { trackEvent } from '../engine/analytics';
import MultiChoiceQuestion from '../components/question/MultiChoiceQuestion';
import ProgressBar from '../components/question/ProgressBar';
import NotFound from './NotFound';
import type { AnswerMap } from '../engine/types';

export default function AssessmentRunner() {
  const { slug = '' } = useParams();
  const schema = getSchemaBySlug(slug);
  const navigate = useNavigate();

  const uuid = schema ? sessionStorage.getItem(`assessment:${schema.id}:uuid`) ?? '' : '';
  const startedAt = schema
    ? sessionStorage.getItem(`assessment:${schema.id}:startedAt`) ?? new Date().toISOString()
    : '';
  const initial: AnswerMap = useMemo(() => {
    if (!schema) return {};
    try {
      return JSON.parse(sessionStorage.getItem(`assessment:${schema.id}:answers`) ?? '{}');
    } catch {
      return {};
    }
  }, [schema]);

  const onAnswer = useCallback(
    (qid: string, oid: string, next: AnswerMap) => {
      if (!schema) return;
      sessionStorage.setItem(`assessment:${schema.id}:answers`, JSON.stringify(next));
      const q = schema.questions.find((x) => x.id === qid);
      trackEvent(`${schema.analytics.eventPrefix}_question_answered`, {
        schema_id: schema.id,
        uuid,
        question_id: qid,
        option_id: oid,
        axis: q?.axis,
        band: q?.band,
        question_index: schema.questions.findIndex((x) => x.id === qid),
      });
    },
    [schema, uuid],
  );

  const runner = useAssessmentRunner({
    schema: schema ?? ({} as never),
    uuid,
    initialAnswers: initial,
    onAnswer,
  });

  useAutosave(
    runner.answers,
    async (answers) => {
      if (!schema || !uuid) return;
      try {
        await saveDraft({
          uuid,
          schemaId: schema.id,
          schemaVersion: schema.version,
          startedAt,
          answers,
        });
      } catch {
        /* swallow; KV rate-limit + retry on next change */
      }
    },
    700,
  );

  useEffect(() => {
    if (schema && runner.isComplete) {
      trackEvent(`${schema.analytics.eventPrefix}_completed`, { schema_id: schema.id, uuid });
      navigate(`/${schema.slug}/intake/`);
    }
  }, [runner.isComplete, navigate, schema, uuid]);

  if (!schema) return <NotFound />;
  if (!runner.currentQuestion) return null;

  return (
    <article className="space-y-6">
      <ProgressBar current={runner.currentIndex + 1} total={runner.totalVisible} />
      <p className="text-sm text-muted">
        Question {runner.currentIndex + 1} of {runner.totalVisible}
      </p>
      <MultiChoiceQuestion
        question={runner.currentQuestion}
        value={runner.answers[runner.currentQuestion.id]}
        onChange={(opt) => runner.answer(runner.currentQuestion!.id, opt)}
      />
      <div className="flex justify-between">
        <button
          type="button"
          onClick={() => runner.previous()}
          disabled={runner.currentIndex === 0}
          className="rounded border border-border px-4 py-2 disabled:opacity-50"
        >
          Back
        </button>
      </div>
    </article>
  );
}
